import { Link } from "@tanstack/react-router";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { MapPin } from "lucide-react";
import { SeoFaq } from "@/components/seo/SeoFaq";
import { SeoLinkList } from "@/components/seo/SeoLinkList";
import {
  breadcrumbListSchema,
  collectionPageSchema,
  faqPageSchema,
  jsonLdScript,
} from "@/lib/seo-schema";
import { INSURANCES, SPECIALTIES } from "@/data/heallexa";
import { SEO_INSURANCE_IDS, specialtySlug, stateNameSlug, toSlug } from "@/lib/seo-slugs";

const SITE = "https://heallexa.com";

export type StateInfo = { code: string; name: string };
export type CityInfo = { name: string; state: string };

/** Same head() shape as locations.$state.tsx, but canonicalized under /providers. */
export function providerStateHeadMeta(state: StateInfo) {
  const title = `Healthcare Providers in ${state.name} | Heallexa`;
  const description = `Find doctors, dentists, therapists, chiropractors, and specialists across ${state.name}. Browse Heallexa's ${state.name} provider directory by city and specialty.`;
  const slug = stateNameSlug(state.name);
  const url = `${SITE}/providers/${slug}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
    ],
    links: [{ rel: "canonical", href: url }],
    scripts: [
      jsonLdScript(collectionPageSchema({ name: title, description, url })),
      jsonLdScript(
        breadcrumbListSchema([
          { name: "Home", url: "/" },
          { name: "Providers", url: "/browse" },
          { name: state.name, url: `/providers/${slug}` },
        ]),
      ),
      jsonLdScript(
        faqPageSchema([
          {
            q: `How do I find a doctor in ${state.name}?`,
            a: `Pick your city on this page to see Heallexa's ${state.name} provider directory. Filter by specialty and insurance to narrow down.`,
          },
          {
            q: `Which insurance plans are accepted in ${state.name}?`,
            a: `Most ${state.name} providers accept major plans including BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare. Confirm with each office.`,
          },
          {
            q: `Is Heallexa free for patients?`,
            a: `Yes. Search, browse, and contact providers in ${state.name} for free, no signup required.`,
          },
        ]),
      ),
    ],
  };
}

export function ProviderStateHub({ state, cities }: { state: StateInfo; cities: CityInfo[] }) {
  const stateSlug = stateNameSlug(state.name);
  const topSpecialties = SPECIALTIES.slice(0, 18);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-[1200px] px-6 pt-32 md:pt-24 pb-16">
        <Breadcrumbs
          className="mb-6"
          items={[
            { name: "Home", href: "/" },
            { name: "Providers", href: "/browse" },
            { name: state.name },
          ]}
        />
        <Reveal from="up">
          <header className="mb-12">
            <p className="font-display text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">{state.code}</p>
            <h1 className="font-display text-[clamp(1.8rem,3vw,2.5rem)] font-semibold tracking-[-0.03em] leading-[1.15] text-foreground">
              Healthcare Providers in {state.name}
            </h1>
            <p className="mt-3 text-[14px] text-muted-foreground max-w-[560px]">
              Browse doctors, dentists, therapists, chiropractors, and specialists across {state.name}.
              Pick a city below to see local providers, or search the full {state.name} directory.
            </p>
            <Link
              to="/search"
              search={{ location: state.name }}
              className="inline-flex items-center justify-center gap-2 mt-6 h-11 px-5 text-[14px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors rounded-md"
            >
              Search all providers in {state.name}
            </Link>
          </header>
        </Reveal>

        {cities.length > 0 && (
          <Reveal from="up" delay={60}>
            <SeoLinkList
              heading={`Cities in ${state.name}`}
              links={cities.map((c) => ({
                label: c.name,
                href: `/providers/${stateSlug}/${toSlug(c.name)}`,
              }))}
              columns={3}
              icon={MapPin}
              className="mb-10"
            />
          </Reveal>
        )}

        <Reveal from="up" delay={60}>
          <SeoLinkList
            heading={`Top specialties in ${state.name}`}
            links={topSpecialties.map((s) => ({
              label: `${s} in ${state.name}`,
              href: `/providers/${specialtySlug(s)}`,
            }))}
            columns={3}
            className="mb-10"
          />
        </Reveal>

        <Reveal from="up" delay={60}>
          <SeoLinkList
            heading={`Insurance carriers in ${state.name}`}
            links={SEO_INSURANCE_IDS.map((id) => INSURANCES.find((i) => i.id === id))
              .filter(Boolean)
              .map((ins) => ({
                label: `${ins!.short} providers in ${state.name}`,
                href: `/insurance/${ins!.id}`,
              }))}
            columns={3}
            className="mb-10"
          />
        </Reveal>

        <SeoFaq
          className="mb-8"
          items={[
            {
              q: `How do I find a doctor in ${state.name}?`,
              a: `Pick your city on this page to browse ${state.name} providers. You can filter by specialty and insurance once you're on the city page.`,
            },
            {
              q: `Which insurance plans are accepted in ${state.name}?`,
              a: `Most ${state.name} providers accept major plans including BCBS, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare.`,
            },
            {
              q: `Is Heallexa free?`,
              a: `Yes — search, browse, and contact providers in ${state.name} for free with no signup required.`,
            },
          ]}
        />
      </main>
      <FooterSection />
    </div>
  );
}
